<template>
  <div
    ref="resource-container"
    class="w-[100vw] overflow-x-hidden overflow-y-scroll flex flex-col lg:flex-1 min-h-screen"
  >
    <div
      class="md:max-w-[856px] md:mx-auto px-6 pt-6 md:pt-[40px] overflow-visible lg:px-0"
    >
      <WhatsNextBanner v-if="showWhatsNextBanner" />
      <div class="flex justify-between">
        <h1
          class="text-grey-100 text-[32px] leading-[40px] font-aktiv-grotesk-medium"
        >
          {{ $t("copy.resourcesPage.title") }}
        </h1>
        <div
          class="w-[108px] flex items-center justify-center gap-[1px] bg-[#E7F7FF] rounded-[24px] cursor-pointer"
          @click="this.$router.push('/resources/sessions/mylist')"
        >
          <img src="/assets/images/bookmarkFilled.svg" alt="bookmarkFilled" />
          <span
            class="text-lg text-lightblue font-semibold font-aktiv-grotex-trial"
          >
            {{ myListHeader }}
          </span>
        </div>
      </div>
      <p
        class="font-aktiv-grotesk text-[16px] leading-[24px] pt-4 pb-6 lg:pb-[40px]"
      >
        {{ $t("copy.resourcesPage.description") }}
      </p>
      <div class="md:flex md:justify-between md:px-0">
        <div class="flex flex-row items-center gap-2 flex-wrap">
          <div
            v-for="button in buttons"
            :key="button.displayText"
            class="border-2 border-lightblue px-3 py-[.375rem] text-center font-semibold font-aktiv-grotex-trial whitespace-nowrap text-lightblue rounded-full transition duration-75 ease-linear cursor-pointer hover:bg-lightblue hover:text-white"
            :class="isHighlight(button) ? '!bg-lightblue !text-white' : ''"
            :data-event-tracking="'filter-' + button.displayText.toLowerCase()"
            :data-test-id="'filter-' + button.displayText.toLowerCase()"
            @click="handleClick(button)"
          >
            {{ button.translatedText }}
          </div>
        </div>
        <div
          class="mt-6 md:mt-0 border-[2px] border-grey-40 text-[#637083] rounded-xl h-[48px] w-full md:w-[400px] flex flex-row items-center py-[10px] px-3 cursor-pointer hover:opacity-80"
          @click="this.$router.push('/resources/sessions/search')"
        >
          <img
            class="mr-2"
            fill="#637083"
            src="/assets/icons/search.svg"
            alt=""
          />
          <p>{{ $t("copy.resourcesPage.searchBar") }}</p>
        </div>
      </div>
    </div>

    <div class="relative w-full">
      <slot></slot>
    </div>

    <div
      v-if="showBackToTop"
      class="flex flex-col items-center justify-center py-6"
    >
      <p
        class="font-aktiv-grotesk-trial text-center xs:text-left font-semibold text-[20px] text-grey-100 sm:mr-2"
      >
        {{ $t("copy.resourcesPage.backToTopSection.otherContentText") }}
      </p>
      <div
        class="flex items-center cursor-pointer hover:opacity-90"
        data-test-id="gensapp-resourcespage-backtotop"
        @click="scrollToTop"
      >
        <p
          class="font-aktiv-grotesk-trial font-semibold text-[20px] text-lightblue mr-2 hover:underline"
        >
          {{ $t("copy.resourcesPage.backToTopSection.backToTop") }}
        </p>
        <img
          class="w-[12px]"
          src="../../../public/assets/images/chevron-up-blue.svg"
          alt=""
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { getResourceFilterButtons } from "@/constants/dropdown.js";
import { bookmarkOutline } from "ionicons/icons";
import WhatsNextBanner from "@/components/WhatsNextBanner.vue";

export default {
  name: "ResourcesContainer",
  components: {
    WhatsNextBanner,
  },
  props: {
    showMoreResources: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showBackToTop: false,
      bookmarkOutlineIcon: bookmarkOutline,
      showWhatsNextBanner: false,
    };
  },
  created() {
    this.checkTimeForBanner();
    this.startInterval();
  },
  mounted() {
    setTimeout(() => {
      this.onResize();
    }, 4000);

    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    clearInterval(this.intervalId);
  },
  computed: {
    ...mapState("cms", ["loading"]),
    ...mapState("filter", ["filteredItems", "selectedFilter"]),
    ...mapGetters("filter", ["myListHeader"]),

    buttons() {
      return getResourceFilterButtons(this.$t);
    },
  },
  watch: {
    loading(newState, oldState) {
      setTimeout(() => {
        this.onResize();
      }, 4000);
    },
  },
  methods: {
    async handleClick(button) {
      await this.$store.dispatch("filter/setClickedFilter", button);
      if (button.displayText === "All") {
        this.resetFilter();
      } else {
        this.openFilterModal(button);
      }
    },
    async resetFilter() {
      await this.$store.dispatch("filter/updateFilteredItems", {
        filteredItems: [],
      });
      await this.$store.dispatch("filter/setSelectedFilter", {});
      await this.$store.commit("filter/clearFilteredResources", {});
    },
    async openFilterModal(button) {
      const showFilterModal = true;
      await this.$store.dispatch("filter/updateShowFilterModal", {
        showFilterModal,
      });
    },
    scrollToTop() {
      const MAP = {
        "/resources/sessions": "contentResourceSessions",
      };

      const elem = document.getElementById(MAP[this.$route.path]);
      elem.scrollToTop(1000);
    },
    onResize() {
      const element = this.$refs["resource-container"];
      if (!element) return (this.showBackToTop = false);

      const screenHeight =
        window.screen.width < 768
          ? window.screen.height
          : window.screen.height - 88;
      this.showBackToTop = element.scrollHeight > screenHeight + 160;
    },
    isHighlight(button) {
      if (button.displayText === "All" && this.filteredItems.length === 0)
        return true;

      if (!this.selectedFilter || Object.keys(this.selectedFilter).length === 0)
        return false;

      const buttonFilterKey = `${button.type}-${button.typeId}`;
      const selectedFilterKey = `${this.selectedFilter.type}-${this.selectedFilter.typeId}`;
      return selectedFilterKey === buttonFilterKey;
    },
    checkTimeForBanner() {
      const now = new Date();
      const targetDate = new Date("2025-08-25T12:00:00+08:00"); // Set your target date and time in SGT

      if (now >= targetDate) {
        this.showWhatsNextBanner = true;
      }
    },
    startInterval() {
      this.intervalId = setInterval(this.checkTimeForBanner, 60000); // Check every minute
    },
  },
};
</script>
